import React, {
    FormEvent, ReactElement, useState,
} from "react";
import { FloatingLabel, Form } from "react-bootstrap";
import Player from "../../components/LottiePlayer";
import lottieImg from "../../assets/lottie/robot.lottie";
import { Loading } from "../../components";
import { Paths, useParams } from "../../contexts/Routing";
import { useGetChannelInviteQuery, useSendChannelInviteMutation } from "../../__generated__/api";
import { NoDataOrError } from "../error/Error";

const SIGN_UP_URL = "https://www.foqal.io?utm_source=app&utm_medium=link&utm_campaign=channel-create&utm_id=channel-create";

export default function ChannelInvite(): ReactElement {
    const { id } = useParams(Paths.CHANNEL_INVITE);
    const { data, loading, error } = useGetChannelInviteQuery({ variables: { id } });
    const [sendChannelInvite] = useSendChannelInviteMutation();
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [company, setCompany] = useState("");
    const [isCompleted, setCompleted] = useState(false);
    const [errorMessage, setError] = useState<string>();

    if (loading) {
        return <Loading />;
    }
    if (error || !data) {
        return <NoDataOrError error={error} />;
    }

    const handleSubmit = async (evt: FormEvent) => {
        evt.preventDefault();
        try {
            await sendChannelInvite({
                variables: {
                    invite: {
                        companyName: company,
                        email,
                        name,
                        inviteId: id,
                    },
                },
            });
        } catch (err) {
            setError((err as {message: string}).message ?? "An error occured.");
            return;
        }

        setCompleted(true);
    };

    const player = (
        <div className="flex-grow-0 w-50 align-self-center border-end pe-3 me-3 d-none d-lg-block">
            <Player
                autoplay
                loop
                src={lottieImg}
                className="player"
            />
        </div>
    );

    if (isCompleted) {
        return (
            <div className="full-page">

                <div className="centered-container pt-5 flex-row">
                    {player}
                    <div className="flex-grow-1 flex-shrink-1">
                        <h3 className="mb-4">
                            Check your email
                        </h3>
                        <div className="font-light mb-3">
                            You should have received an email from Slack. This email will have a link to accept
                            the Shared Channel invite. If you do not see an email from
                            {" "}
                            <code>notification@slack-mail.com</code>
                            {" "}
                            please check your spam folder.
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="full-page">

            <div className="centered-container pt-5 flex-row">
                {player}
                <div className="flex-grow-1 flex-shrink-1">

                    <h3 className="mb-4">
                        Slack Connect with
                        {" "}
                        {data.channelInvite?.name}
                    </h3>
                    <div className="font-light mb-3">
                        You have been invited to participate in a Slack Connect Channel with
                        {" "}
                        {data.channelInvite?.name}
                        . Enter your name, email, and company name to get started.
                    </div>
                    <form onSubmit={handleSubmit}>

                        <FloatingLabel
                            controlId="name"
                            label="Name"
                            className="mb-3"
                        >
                            <Form.Control
                                type="text"
                                required
                                placeholder="Sara Langa"
                                data-test="name"
                                isInvalid={errorMessage != null}
                                onChange={evt => setName(evt.target.value)}
                            />
                        </FloatingLabel>

                        <FloatingLabel
                            controlId="email"
                            label="Email"
                            className="mb-3"
                        >
                            <Form.Control
                                type="email"
                                required
                                placeholder="name@example.com"
                                data-test="email"
                                isInvalid={errorMessage != null}
                                onChange={evt => setEmail(evt.target.value)}
                            />
                        </FloatingLabel>

                        <FloatingLabel
                            controlId="company"
                            label="Company Name"
                            className="mb-3"
                        >
                            <Form.Control
                                type="text"
                                placeholder="Acme Corp"
                                data-test="company"
                                required
                                isInvalid={errorMessage != null}
                                onChange={evt => setCompany(evt.target.value)}
                            />
                        </FloatingLabel>

                        {errorMessage && (
                            <div className="d-flex alert alert-danger">
                                {errorMessage}
                            </div>
                        )}

                        <div className="d-flex justify-content-end">
                            <button
                                data-test="submit-btn"
                                type="submit"
                                className="btn btn-md btn-primary submit-btn mt-3"
                            >
                                Next
                            </button>
                        </div>
                    </form>

                </div>

            </div>

            <small className="text-light-400 d-block mt-5 text-center">
                <p className="mb-2">
                    Powered by
                    {" "}
                    <a
                        href={SIGN_UP_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="text-light-400"
                    >
                        foqal.io
                    </a>
                    .
                    {" "}
                </p>
                <p>
                    To create your own Slack Shared Channel Invite Page,
                    {" "}
                    <a
                        href={SIGN_UP_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="text-light-400"
                    >
                        sign up today
                    </a>
                    .
                </p>
            </small>
        </div>
    );
}
